// src/gp.ts —— H5 服务（TD-806）：API 客户端、会话、UI 原语；宿主在 shell.mount 时绑定
import { ApiClient, ApiError } from '../../shared/src/api/client';
import { isNotReady } from '../../shared/src/api/pending';
import type { DcHost, Dict, EventBus } from '../../shared/src/dc';
import { errorText, type ErrContext } from '../../shared/src/errors';
import { TOKEN_KEYS, localTokenStore, type TokenStore } from '../../shared/src/storage';
import { documentVisibility, type VisibilitySource } from '../../shared/src/poll';
import { haptic, isMiniApp } from '../../shared/src/tg';
import { GATE_MODAL, gateErrorMissing, gateMissing, type GateMissing } from './domains/gate';
import type { H5State, ModalState, Screen } from './state';

export interface H5Ui {
  toast(msg: string, kind?: 'success' | 'error'): void;
  go(screen: Screen): void;
  openModal(m: ModalState): void;
  closeModal(): void;
  copy(text: string): void;
  // 全局 busy 互斥（U-20）：已有在途提交时返回 undefined 不执行
  run<T>(key: string, fn: () => Promise<T>): Promise<T | undefined>;
  // 外层 run 内部的子步骤（U-21 动态码提交）：不受外层占用拦截，执行期间 busy 置为本键，结束后恢复进入前的键——
  // 外层互斥不因子步骤结束而提前解除（直接写 busy:'' 会让外层在途期间重新可点）
  nest<T>(key: string, fn: () => Promise<T>): Promise<T>;
  err(e: unknown, vars?: Readonly<Record<string, string | number | undefined>>, ctx?: ErrContext): string;
  dict(): Dict;
}

export interface H5Session {
  // rollbackOnFail：交互式登录（邮箱 / 注册 / TG 按钮）在 GET /profile 失败时撤回已写入的 token，避免刷新后静默恢复半建立的会话；boot 恢复路径不回滚
  established(token: string, opts: { persist: boolean; toast?: string; termsBanner?: boolean; rollbackOnFail?: boolean }): Promise<void>;
  reloadProfile(): Promise<void>;
  setLang(lang: 'zh' | 'en'): void;
  logout(): Promise<void>;
}

export interface H5Totp {
  // 已开 2FA 时弹 U-21 收码后执行 action(code)；未开 2FA 直接 action(undefined)；用户取消返回 null
  run<T>(action: (totp: string | undefined) => Promise<T>, opts?: { waitNext?: boolean }): Promise<T | null>;
}

// U-22 E-05：回前台（visibilitychange → visible）时各域重拉当前页查询；shell 负责会话判断与 GET /profile
export interface H5Foreground {
  on(fn: () => void): () => void;
  fire(): void;
  watch(onVisible: () => void): () => void;
}

// 跨域导航入口（06 U-12 通知跳转）：目标域在 mount 时提供；未提供时调用方回落到屏级跳转
export interface H5Nav {
  provide(p: Partial<Omit<H5Nav, 'provide'>>): void;
  openCard(cardId: number): boolean;
  openLedger(tab: 'deposit' | 'fee'): boolean;
  /** 进入选卡段（开卡流程首屏，含开关检查与卡段加载）；充值成功页「去开卡」用 */
  openProducts(): boolean;
}

// 06 U-24 资金动作拦截（2026-10-17）：未开通邮箱 + 密码登录的会话不发资金写请求，改弹引导；服务端 1015 同样弹引导
export interface H5Gate {
  missing(): GateMissing | null;
  // 受限 → 打开引导弹层并返回 true（调用方随即 return，不发请求）；不受限返回 false
  guard(): boolean;
  open(missing: GateMissing): void;
}

export interface H5Gp {
  api: ApiClient;
  gate: H5Gate;
  tokens: TokenStore;
  events: EventBus;
  ui: H5Ui;
  session: H5Session;
  totp: H5Totp;
  foreground: H5Foreground;
  nav: H5Nav;
  bind(host: DcHost<H5State>): void;
  host(): DcHost<H5State>;
}

const TOAST_MS = 2200; // 原型 L592 toast 停留时长

function createForeground(vis: VisibilitySource): H5Foreground {
  const subs = new Set<() => void>();
  return {
    on(fn) {
      subs.add(fn);
      return () => {
        subs.delete(fn);
      };
    },
    fire() {
      for (const fn of [...subs]) {
        try {
          fn();
        } catch (e) {
          console.error('[gp:h5] 回前台重拉失败', e);
        }
      }
    },
    watch(onVisible) {
      const unsub = vis.subscribe(() => {
        if (!vis.isHidden()) onVisible();
      });
      // 宿主卸载晚于文档环境销毁时（测试桩 / 页面卸载），退订失败不影响卸载流程
      return () => {
        try {
          unsub();
        } catch (e) {
          console.warn('[gp:h5] 回前台监听退订失败', e);
        }
      };
    },
  };
}

function createNav(): H5Nav {
  let openCard: ((id: number) => boolean) | null = null;
  let openLedger: ((tab: 'deposit' | 'fee') => boolean) | null = null;
  let openProducts: (() => boolean) | null = null;
  return {
    provide(p) {
      if (p.openCard) openCard = p.openCard;
      if (p.openLedger) openLedger = p.openLedger;
      if (p.openProducts) openProducts = p.openProducts;
    },
    openCard: (id) => openCard?.(id) ?? false,
    openLedger: (tab) => openLedger?.(tab) ?? false,
    openProducts: () => openProducts?.() ?? false,
  };
}

export function createServices(
  events: EventBus,
  impl: { session: () => H5Session; totp: () => H5Totp; dict: () => Dict },
  vis: VisibilitySource = documentVisibility,
): H5Gp {
  let current: DcHost<H5State> | null = null;
  const host = (): DcHost<H5State> => {
    if (!current) throw new Error('H5 宿主尚未绑定（shell.mount 之前调用了服务）');
    return current;
  };
  const tokens = localTokenStore(TOKEN_KEYS.user);
  const api = new ApiClient({
    base: '',
    aud: 'user',
    tokens,
    isMiniApp: () => isMiniApp(),
    // H5 不区分空闲 / 最长在线（2026-10-15：H5 只把 refresh 改 7 天、不加空闲提示）：带原因的会话终止一律按 expired 回登录页
    onSessionLost: (reason) => events.emit('session:lost', { reason: reason === 'idle_timeout' || reason === 'max_age' ? 'expired' : reason }),
    log: (event, detail) => console.warn('[gp:api]', event, detail),
  });
  const gate: H5Gate = {
    missing: () => gateMissing(current?.state.sh.profile),
    guard() {
      const m = gate.missing();
      if (!m) return false;
      gate.open(m);
      return true;
    },
    open(missing) {
      haptic('error');
      host().setState({ modal: { type: GATE_MODAL, title: String(impl.dict().gateTitle ?? ''), missing } });
    },
  };
  // 服务端 1015：本地 profile 可能已过期（如上线前取的 profile 无受限字段）——刷新 profile 让提示条出现，并弹引导。
  // 延到微任务：调用方常在取文案后同步关闭当前弹层（如卡操作确认框），引导须在其后打开才不被关掉
  const onGateError = (missing: GateMissing): void => {
    queueMicrotask(() => {
      try {
        gate.open(missing);
      } catch (e) {
        console.warn('[gp:h5] 引导弹层打开失败', e);
      }
      impl
        .session()
        .reloadProfile()
        .catch((e: unknown) => console.warn('[gp:h5] 1015 后刷新 profile 失败', e));
    });
  };
  const ui: H5Ui = {
    toast(msg, kind = 'success') {
      if (!msg) return;
      haptic(kind);
      const h = host();
      const id = h.state.toastId + 1;
      h.setState({ toast: msg, toastId: id });
      setTimeout(() => {
        if (h.state.toastId === id) h.setState({ toast: '' });
      }, TOAST_MS);
    },
    go(screen) {
      host().setState({ screen });
    },
    openModal(m) {
      host().setState({ modal: m });
    },
    closeModal() {
      host().setState({ modal: null });
    },
    copy(text) {
      try {
        const p = navigator.clipboard?.writeText(String(text));
        p?.catch((e: unknown) => console.warn('[gp:h5] clipboard', e));
      } catch {
        // 剪贴板不可用时仍给出原型提示
      }
      ui.toast(String(impl.dict().toastCopied ?? ''));
    },
    async run(key, fn) {
      const h = host();
      if (h.state.busy) return undefined;
      h.setState({ busy: key });
      try {
        return await fn();
      } finally {
        h.setState({ busy: '' });
      }
    },
    async nest(key, fn) {
      const h = host();
      const outer = h.state.busy;
      h.setState({ busy: key });
      try {
        return await fn();
      } finally {
        h.setState({ busy: outer });
      }
    },
    err(e, vars, ctx) {
      const d = impl.dict();
      const gm = gateErrorMissing(e, current?.state.sh.profile);
      if (gm) onGateError(gm);
      if (e instanceof ApiError) return errorText('user', e, d, vars, ctx);
      if (isNotReady(e)) return String(d.featurePending ?? ''); // TD-813：未就绪端点统一「该功能即将开放」，不与业务开关关闭（errSwitchOff）混用
      console.error('[gp:h5]', e);
      return String(d.errNetwork ?? '');
    },
    dict: impl.dict,
  };
  return {
    api,
    gate,
    tokens,
    events,
    ui,
    foreground: createForeground(vis),
    nav: createNav(),
    get session() {
      return impl.session();
    },
    get totp() {
      return impl.totp();
    },
    bind(h) {
      current = h;
    },
    host,
  };
}
